import React, { useState, useEffect, useRef, useCallback } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  getDocs,
  limit,
  orderBy,
  where
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import styles from "../styles/SearchBar.module.css";

export default function SearchBar() {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  const navigate = useNavigate();
  const searchRef = useRef(null);

  // Load recent searches from localStorage
  useEffect(() => {
    const savedSearches = localStorage.getItem('recentSearches');
    if (savedSearches) {
      setRecentSearches(JSON.parse(savedSearches));
    }

    const savedHistory = localStorage.getItem('searchHistory');
    if (savedHistory) {
      setSearchHistory(JSON.parse(savedHistory));
    }
  }, []);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Save search to history
  const saveToHistory = (query, type, id) => {
    const newSearch = {
      query,
      type,
      id,
      timestamp: Date.now()
    };

    const updatedHistory = [
      newSearch,
      ...searchHistory.filter(s => s.id !== id).slice(0, 9)
    ];

    setSearchHistory(updatedHistory);
    localStorage.setItem('searchHistory', JSON.stringify(updatedHistory));
  };

  // Save recent search
  const saveRecentSearch = (query) => {
    const updatedSearches = [
      query,
      ...recentSearches.filter(s => s !== query).slice(0, 4)
    ];
    setRecentSearches(updatedSearches);
    localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
  };

  // Enhanced search function with fuzzy matching
  // Enhanced search function with prefix matching
  // Enhanced search function with safe fallback
  const performSearch = useCallback(async (term) => {
    if (term.trim().length < 1) {
      setResults([]);
      return;
    }

    setLoading(true);
    const lowerTerm = term.toLowerCase();

    try {
      // STRATEGY: Fetch a reasonable batch of data and filter client-side.
      // This circumvents Firestore's case-sensitivity and missing index issues.
      // For a production app with millions of users, we would need 'username_lower' fields.
      // But for this scale, fetching 50-100 docs is perfectly fine and provides the best UX.

      const usersQuery = query(collection(db, "users"), limit(50));
      const clubsQuery = query(collection(db, "clubs"), limit(20));
      const groupsQuery = query(collection(db, "groups"), limit(20)); // Security rules might filter this to only joined groups
      const postsQuery = query(collection(db, "posts"), orderBy("createdAt", "desc"), limit(50)); // Changed from 'club_posts' to 'posts' to match PostFeed

      const [usersResult, clubsResult, groupsResult, postsResult] = await Promise.allSettled([
        getDocs(usersQuery),
        getDocs(clubsQuery),
        getDocs(groupsQuery),
        getDocs(postsQuery)
      ]);

      // --- PROCESS USERS ---
      let userResults = [];
      if (usersResult.status === 'fulfilled') {
        userResults = usersResult.value.docs
          .map(doc => ({ id: doc.id, type: "user", ...doc.data() }))
          .filter(user => {
            const u = (user.username || "").toLowerCase();
            const d = (user.displayName || "").toLowerCase();
            const e = (user.email || "").toLowerCase();
            return u.includes(lowerTerm) || d.includes(lowerTerm) || e.includes(lowerTerm);
          })
          .slice(0, 5);
      } else {
        console.warn("Users search failed:", usersResult.reason);
      }

      // --- PROCESS CLUBS ---
      let clubResults = [];
      if (clubsResult.status === 'fulfilled') {
        clubResults = clubsResult.value.docs
          .map(doc => ({ id: doc.id, type: "club", ...doc.data() }))
          .filter(club => {
            const n = (club.name || "").toLowerCase();
            const d = (club.description || "").toLowerCase();
            return n.includes(lowerTerm) || d.includes(lowerTerm);
          })
          .slice(0, 3);
      } else {
        console.warn("Clubs search failed:", clubsResult.reason);
      }

      // --- PROCESS GROUPS ---
      let groupResults = [];
      if (groupsResult.status === 'fulfilled') {
        groupResults = groupsResult.value.docs
          .map(doc => ({ id: doc.id, type: "group", ...doc.data() }))
          .filter(group => {
            const n = (group.name || "").toLowerCase();
            return n.includes(lowerTerm);
          })
          .slice(0, 3);
      } else {
        // This is expected if security rules block listing all groups
        console.warn("Groups search restricted/failed:", groupsResult.reason);
      }

      // --- PROCESS POSTS ---
      let postResults = [];
      if (postsResult.status === 'fulfilled') {
        postResults = postsResult.value.docs
          .map(doc => ({ id: doc.id, type: "post", ...doc.data() }))
          .filter(post => {
            return (post.text || "").toLowerCase().includes(lowerTerm);
          })
          .slice(0, 3);
      } else {
        console.warn("Posts search failed:", postsResult.reason);
      }

      // Combine all results
      const combinedResults = [
        ...userResults,
        ...clubResults,
        ...groupResults,
        ...postResults
      ];

      setResults(combinedResults);
      if (combinedResults.length > 0) {
        saveRecentSearch(term);
      }
    } catch (err) {
      console.error("Search error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTerm.trim().length < 1) {
      setResults([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      performSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, performSearch]);

  const handleResultClick = (result) => {
    if (result.type === "user") {
      if (result.email) {
        navigate(`/profile/${encodeURIComponent(result.email)}`);
      } else {
        // Fallback if email is missing (unlikely given AuthContext/Profile structure)
        console.warn("User result missing email:", result);
      }
    } else if (result.type === "club") {
      navigate(`/club/${result.id}`);
    } else if (result.type === "group") {
      navigate(`/groups`, { state: { groupId: result.id } });
    } else if (result.type === "post") {
      navigate(`/club/${result.clubId}`);
    }

    saveToHistory(searchTerm, result.type, result.id);
    setSearchTerm("");
    setShowResults(false);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setResults([]);
    setShowResults(false);
  };

  const clearHistory = () => {
    setRecentSearches([]);
    setSearchHistory([]);
    localStorage.removeItem('recentSearches');
    localStorage.removeItem('searchHistory');
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'coding': return '💻';
      case 'sports': return '⚽';
      case 'music': return '🎵';
      case 'art': return '🎨';
      case 'gaming': return '🎮';
      case 'education': return '📚';
      case 'business': return '💼';
      case 'entertainment': return '🎬';
      default: return '👥';
    }
  };

  return (
    <div className={styles.container} ref={searchRef}>
      <div className={styles.inputWrapper}>
        <div className={styles.searchIcon}>🔍</div>
        <input
          type="text"
          placeholder="Search users, clubs, posts..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowResults(true);
          }}
          onFocus={() => setShowResults(true)}
          className={styles.input}
        />
        {searchTerm && (
          <button
            onClick={clearSearch}
            className={styles.clearBtn}
          >
            ✕
          </button>
        )}
      </div>

      {showResults && (
        <div className={styles.dropdown}>
          {searchTerm.trim().length === 0 ? (
            /* Recent Searches & History */
            <div className={styles.suggestions}>
              {recentSearches.length > 0 && (
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Recent Searches</div>
                  {recentSearches.map((search, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSearchTerm(search);
                        performSearch(search);
                      }}
                      className={styles.suggestionItem}
                    >
                      <span className={styles.suggestionIcon}>🕒</span>
                      <span>{search}</span>
                    </button>
                  ))}
                  <button
                    onClick={clearHistory}
                    className={styles.clearHistoryBtn}
                  >
                    Clear History
                  </button>
                </div>
              )}

              <div className={styles.section}>
                <div className={styles.sectionTitle}>Popular Now</div>
                <button className={styles.suggestionItem}>
                  <span className={styles.suggestionIcon}>🔥</span>
                  <span>Trending Posts</span>
                </button>
                <button className={styles.suggestionItem}>
                  <span className={styles.suggestionIcon}>👥</span>
                  <span>New Clubs</span>
                </button>
                <button className={styles.suggestionItem}>
                  <span className={styles.suggestionIcon}>💻</span>
                  <span>Tech Clubs</span>
                </button>
              </div>
            </div>
          ) : loading ? (
            <div className={styles.loading}>
              {/* <div className="loading-spinner"></div>  Replaced with text or simple loader */}
              <span>Searching...</span>
            </div>
          ) : results.length === 0 ? (
            <div className={styles.noResults}>
              <div className={styles.noResultsIcon}>🔍</div>
              <div className={styles.noResultsText}>
                <h4>No results found for "{searchTerm}"</h4>
                <p>Try searching for something else</p>
              </div>
            </div>
          ) : (
            <div className={styles.suggestions}> {/* Reuse suggestions padding */}
              {/* Users Section */}
              {results.filter(r => r.type === "user").length > 0 && (
                <div className={styles.resultsSection}>
                  <div className={styles.sectionTitle}>
                    <span className={styles.sectionIcon}>👤</span>
                    Users
                  </div>
                  {results
                    .filter(r => r.type === "user")
                    .map(user => (
                      <div
                        key={user.id}
                        onClick={() => handleResultClick(user)}
                        className={styles.resultItem}
                      >
                        <div className={styles.resultAvatar}>
                          {user.photoURL ? (
                            <img src={user.photoURL} alt={user.username} />
                          ) : (
                            <div className={styles.avatarPlaceholder}>
                              {user.username?.charAt(0)?.toUpperCase() ||
                                user.email?.charAt(0)?.toUpperCase() || "U"}
                            </div>
                          )}
                        </div>
                        <div className={styles.resultInfo}>
                          <div className={styles.resultName}>
                            {user.displayName || user.username || user.email.split('@')[0]}
                          </div>
                          <div className={styles.resultDetails}>
                            @{user.username || user.email.split('@')[0]}
                            {user.branch && (
                              <span className={styles.resultTag}>{user.branch}</span>
                            )}
                          </div>
                        </div>
                        <div className={styles.resultAction}>
                          <span className={styles.resultType}>User</span>
                        </div>
                      </div>
                    ))
                  }
                </div>
              )}

              {/* Groups Section */}
              {results.filter(r => r.type === "group").length > 0 && (
                <div className={styles.resultsSection}>
                  <div className={styles.sectionTitle}>
                    <span className={styles.sectionIcon}>🛡️</span>
                    Groups
                  </div>
                  {results
                    .filter(r => r.type === "group")
                    .map(group => (
                      <div
                        key={group.id}
                        onClick={() => handleResultClick(group)}
                        className={styles.resultItem}
                      >
                        <div className={styles.resultAvatar}>
                          <div className={styles.avatarPlaceholder}>G</div>
                        </div>
                        <div className={styles.resultInfo}>
                          <div className={styles.resultName}>{group.name}</div>
                          <div className={styles.resultDetails}>{group.members?.length || 0} members</div>
                        </div>
                        <div className={styles.resultAction}>
                          <span className={styles.resultType}>Group</span>
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {/* Posts Section */}
              {results.filter(r => r.type === "post").length > 0 && (
                <div className={styles.resultsSection}>
                  <div className={styles.sectionTitle}>
                    <span className={styles.sectionIcon}>📝</span>
                    Posts
                  </div>
                  {results
                    .filter(r => r.type === "post")
                    .map(post => (
                      <div
                        key={post.id}
                        onClick={() => handleResultClick(post)}
                        className={styles.resultItem}
                      >
                        <div className={styles.resultAvatar}>
                          <div className={styles.avatarPlaceholder}>P</div>
                        </div>
                        <div className={styles.resultInfo}>
                          <div className={styles.resultName}>{post.text?.substring(0, 40)}...</div>
                          <div className={styles.resultDetails}>in {post.clubName || 'Unknown Club'}</div>
                        </div>
                        <div className={styles.resultAction}>
                          <span className={styles.resultType}>Post</span>
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {/* Clubs Section */}
              {results.filter(r => r.type === "club").length > 0 && (
                <div className={styles.resultsSection}>
                  <div className={styles.sectionTitle}>
                    <span className={styles.sectionIcon}>👥</span>
                    Clubs
                  </div>
                  {results
                    .filter(r => r.type === "club")
                    .map(club => (
                      <div
                        key={club.id}
                        onClick={() => handleResultClick(club)}
                        className={styles.resultItem}
                      >
                        <div className={styles.resultAvatar}>
                          {club.logoUrl ? (
                            <img src={club.logoUrl} alt={club.name} />
                          ) : (
                            <div className={styles.avatarPlaceholder}>
                              {getCategoryIcon(club.category)}
                            </div>
                          )}
                        </div>
                        <div className={styles.resultInfo}>
                          <div className={styles.resultName}>{club.name}</div>
                          <div className={styles.resultDetails}>
                            <span className={styles.memberCount}>
                              👥 {club.memberCount || 0} members
                            </span>
                            <span className={styles.resultTag}>
                              {club.privacy === 'public' ? '🌐 Public' : '🔒 Private'}
                            </span>
                            {club.category && (
                              <span className={styles.resultTag}>
                                {club.category}
                              </span>
                            )}
                          </div>
                          {club.description && (
                            <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
                              {club.description.length > 60
                                ? `${club.description.substring(0, 60)}...`
                                : club.description
                              }
                            </div>
                          )}
                        </div>
                        <div className={styles.resultAction}>
                          <span className={styles.resultType}>Club</span>
                        </div>
                      </div>
                    ))
                  }
                </div>
              )}

              {/* View All Results */}
              {results.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <button
                    onClick={() => {
                      // Navigate to full search results page
                      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
                      setShowResults(false);
                    }}
                    className={styles.viewAllBtn}
                  >
                    View all results for "{searchTerm}"
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}